import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
function buildYears() { const y = new Date().getFullYear(); return [y - 2, y - 1, y, y + 1]; }

// Nm³ capacity per fermenter gas holder
const FERMENTER_CAP_NM3 = 8350;

// ── Formatters ────────────────────────────────────────────────────────────────
const fc = (v: number | null | undefined, dec = 0) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPeriodStart = (year: number, month: number) =>
  new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
const fmtPeriodEnd = (year: number, month: number) =>
  new Date(year, month, 0).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
const fmtDayLabel = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  const DAY = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return `${d.getDate()}-${MONTHS[d.getMonth()].substring(0,3)}-${DAY[d.getDay()]}`;
};
const REPORT_DATE = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });

// ── Table cell styles ─────────────────────────────────────────────────────────
const NAVY = '#1E2D78';
const TH: React.CSSProperties = {
  background: NAVY, color: '#fff', fontWeight: 700,
  border: '1px solid #0d1b57', padding: '4px 5px', fontSize: '8.5px',
  textAlign: 'center', whiteSpace: 'nowrap', lineHeight: 1.25,
};
const TD: React.CSSProperties = {
  border: '1px solid #ccc', padding: '3px 5px', fontSize: '8.5px',
  textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle',
};
const TH_BILLING: React.CSSProperties = {
  background: NAVY, color: '#fff', fontWeight: 700,
  border: '1px solid #0d1b57', padding: '5px 8px', fontSize: '9px', textAlign: 'left',
};
const TD_BILLING: React.CSSProperties = {
  border: '1px solid #ccc', padding: '4px 8px', fontSize: '9px', textAlign: 'right',
};
const TD_BILLING_L: React.CSSProperties = { ...TD_BILLING, textAlign: 'left' };

// ── Compute total gas storage Nm³ from fermenter %s ───────────────────────────
const gasInventoryNm3 = (row: any): number | null => {
  const f1 = row?.f1GasStoragePct, f2 = row?.f2GasStoragePct, f3 = row?.f3GasStoragePct;
  if (f1 == null && f2 == null && f3 == null) return null;
  return ((f1 ?? 0) + (f2 ?? 0) + (f3 ?? 0)) / 100 * FERMENTER_CAP_NM3;
};

// ── Signature block ───────────────────────────────────────────────────────────
const SigBlock: React.FC<{ label: string; name: string; title: string; date?: string }> = ({ label, name, title, date }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 170 }}>
    <Typography sx={{ fontSize: '9px' }}>{label}:</Typography>
    <Box sx={{ borderBottom: '1px solid #000', width: '100%', minHeight: 26 }} />
    <Typography sx={{ fontSize: '9px', fontWeight: 700, borderBottom: '1px solid #000' }}>{name}</Typography>
    <Typography sx={{ fontSize: '9px' }}>{title}</Typography>
    {date && <Typography sx={{ fontSize: '8px', color: '#555' }}>{date}</Typography>}
  </Box>
);

// ── Billing Invoice Page (pages 1 & 2) ────────────────────────────────────────
interface BillingRow { label: string; final: number | null; initial: number | null; consumed: number | null; unit: string }
const BillingInvoice: React.FC<{
  pageNum: number; year: number; month: number; type: 'power' | 'gas';
  rows: BillingRow[]; totalConsumed: number; unitPrice: number; totalAmount: number;
}> = ({ pageNum, year, month, type, rows, totalConsumed, unitPrice, totalAmount }) => {
  const unit      = type === 'power' ? 'kWh' : 'Nm³';
  const billingRe = type === 'power' ? 'Power Supplied to DPO Plant' : 'Gas Supplied to DPO Boiler';
  const calcText  = type === 'power' ? 'Power Consumption' : 'Gas Consumption';
  const tableTitle = type === 'power' ? 'Power Particulars' : 'Boiler Particulars';
  const totalLabel = type === 'power' ? 'Total Power Produced:' : 'Total Boiler Gas Consumption:';
  const consumedLabel = type === 'power' ? 'Power Produced:' : 'Boiler Gas Consumption:';
  return (
    <Box sx={{ p: '14mm', fontFamily: 'Arial, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      <Typography sx={{ fontWeight: 800, fontSize: '11px', mb: 0.5 }}>SURALLAH BIOGAS VENTURES CORP.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 4, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '9px' }}>Billed to: Dole Philippines, Inc.</Typography>
          <Typography sx={{ fontSize: '9px' }}>Billing re: {billingRe}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
            <Typography sx={{ fontSize: '9px' }}>Period Covered:</Typography>
            <Typography sx={{ fontSize: '9px' }}>{fmtPeriodStart(year, month)}</Typography>
            <Typography sx={{ fontSize: '9px' }}>to</Typography>
            <Typography sx={{ fontSize: '9px' }}>{fmtPeriodEnd(year, month)}</Typography>
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '9px' }}>Date:&nbsp;&nbsp;&nbsp;&nbsp;{REPORT_DATE}</Typography>
          <Typography sx={{ fontSize: '9px' }}>Page&nbsp;&nbsp;&nbsp;&nbsp;{pageNum}/1</Typography>
        </Box>
      </Box>

      <Typography sx={{ fontWeight: 700, fontSize: '9px', mb: 0.5, mt: 2 }}>SUPPORTING CALCULATION FOR:</Typography>
      <Typography sx={{ fontSize: '9px', mb: 1.5 }}>{calcText} = Final Meter Reading - Initial Meter Reading</Typography>

      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 20 }}>
        <thead>
          <tr>
            <th style={{ ...TH_BILLING, width: '28%' }}>{tableTitle}</th>
            <th style={TH_BILLING}>Final Meter Reading</th>
            <th style={TH_BILLING}>Initial Meter Reading</th>
            <th style={TH_BILLING}>Consumed Gas</th>
            <th style={TH_BILLING}>Units</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.label}>
              <td style={TD_BILLING_L}>{r.label}</td>
              <td style={TD_BILLING}>{r.final != null ? fc(r.final) : ''}</td>
              <td style={TD_BILLING}>{r.initial != null ? fc(r.initial) : ''}</td>
              <td style={TD_BILLING}>{r.consumed != null ? fc(r.consumed) : ''}</td>
              <td style={TD_BILLING}>{r.unit}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} style={{ ...TH_BILLING, fontWeight: 800 }}>{totalLabel}</td>
            <td style={{ ...TH_BILLING, textAlign: 'center', fontWeight: 800 }}>{fc(totalConsumed)} {unit}</td>
            <td style={{ background: NAVY, border: '1px solid #0d1b57' }} />
          </tr>
        </tbody>
      </table>

      <Box sx={{ ml: 6, maxWidth: 340 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '9px' }}>{consumedLabel}</Typography>
          <Typography sx={{ fontSize: '9px' }}>{fc(totalConsumed)} {unit}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: '9px' }}>Unit Price</Typography>
          <Typography sx={{ fontSize: '9px' }}>{unitPrice} Php/{unit}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', background: '#FFD700', p: '5px 8px', border: '1px solid #bbb' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '9px' }}>Total Amount Due</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '9px' }}>{fc(totalAmount, 2)}&nbsp;Php</Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 7, display: 'flex', justifyContent: 'space-between' }}>
        <SigBlock label="Prepared By" name="Francis David A. Requillo" title="SBVC Representative" />
        <SigBlock label="Approved By" name="Veerland Dongon" title="SBVC Representative" />
      </Box>
      <Box sx={{ mt: 4 }}>
        <SigBlock label="Received By" name="Toribio G. Castro" title="Dole Representative" date={REPORT_DATE} />
      </Box>
    </Box>
  );
};

// ── Production Monitoring Table (pages 3 & 4) ─────────────────────────────────
const ProductionTable: React.FC<{
  rows: any[];
  methaneMap: Record<string, number | null>;
  prevDayRow?: any;
  summaryLabel?: string;   // e.g. 'Subtotal' or 'Total'
  summaryRow?: any;
  summaryMetadata?: any;   // for page 4 bottom summary section
  year: number; month: number; docNum: number;
}> = ({ rows, methaneMap, prevDayRow, summaryLabel, summaryRow, summaryMetadata, year, month, docNum }) => {

  const renderRow = (row: any, label?: string, bold = false, bg = 'transparent') => {
    const inv = gasInventoryNm3(row);
    const ch4 = methaneMap[row?.readingDate ?? ''] ?? null;
    const flareTime = row?.gasFlareStartTime
      ? (row.gasFlareEndTime ? `${row.gasFlareStartTime}–${row.gasFlareEndTime}` : row.gasFlareStartTime)
      : '';
    const cell = (v: any, dec = 0) => (
      <td style={{ ...TD, fontWeight: bold ? 700 : undefined, background: bg }}>
        {v == null ? '' : (typeof v === 'number' ? fc(v, dec) : v)}
      </td>
    );
    return (
      <tr key={label ?? row?.readingDate}>
        <td style={{ ...TD, textAlign: 'left', fontWeight: bold ? 700 : undefined, background: bg }}>
          {label ?? fmtDayLabel(row.readingDate)}
        </td>
        {cell(row?.engine1Totalizer)}{cell(row?.engine1Output)}
        {cell(row?.engine2Totalizer)}{cell(row?.engine2Output)}
        {cell(row?.f1GasStoragePct)}{cell(row?.f2GasStoragePct)}{cell(row?.f3GasStoragePct)}
        {cell(inv)}
        {cell(ch4)}
        {cell(row?.thermax1Totalizer)}{cell(row?.thermax1Output)}
        {cell(row?.thermax2Totalizer)}{cell(row?.thermax2Output)}
        <td style={{ ...TD, fontWeight: bold ? 700 : undefined, background: bg }}>{flareTime}</td>
        {cell(row?.gasFlareTotalVolume)}
        <td style={{ ...TD, background: bg }}>{row?.remarks ?? ''}</td>
      </tr>
    );
  };

  return (
    <Box sx={{ p: '5mm 6mm', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box', width: '100%' }}>
      {/* Report header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '11px', lineHeight: 1.2 }}>SURALLAH BIOGAS</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '11px', lineHeight: 1.2 }}>VENTURES CORP.</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', flex: 1, mx: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '14px' }}>DAILY PRODUCTION MONITORING</Typography>
          <Typography sx={{ fontSize: '10px' }}>Polomolok Biogas Facility</Typography>
        </Box>
        <Box sx={{ border: '1px solid #bbb', px: 1.5, py: 0.5, borderRadius: 0.5 }}>
          <Typography sx={{ fontSize: '8px', color: '#555', fontWeight: 600 }}>METPower</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: '9px' }}>Document No.&nbsp;&nbsp;<strong>{docNum}</strong></Typography>
        <Typography sx={{ fontSize: '9px' }}>Period:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<strong>{fmtPeriodStart(year, month)}</strong></Typography>
        <Typography sx={{ fontSize: '9px' }}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<strong>{fmtPeriodEnd(year, month)}</strong></Typography>
      </Box>

      {/* Table */}
      <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
        <thead>
          <colgroup>
            <col style={{ width: '8.5%' }} />{/* Date */}
            <col style={{ width: '6.5%' }} />{/* Eng1 Totalizer */}
            <col style={{ width: '5.5%' }} />{/* Eng1 Output */}
            <col style={{ width: '6.5%' }} />{/* Eng2 Totalizer */}
            <col style={{ width: '5.5%' }} />{/* Eng2 Output */}
            <col style={{ width: '4.5%' }} />{/* F1 */}
            <col style={{ width: '4.5%' }} />{/* F2 */}
            <col style={{ width: '4.5%' }} />{/* F3 */}
            <col style={{ width: '6%' }} />{/* Total */}
            <col style={{ width: '5.5%' }} />{/* % Methane */}
            <col style={{ width: '7%' }} />{/* Thx1 Totalizer */}
            <col style={{ width: '6%' }} />{/* Thx1 Consumed */}
            <col style={{ width: '7%' }} />{/* Thx2 Totalizer */}
            <col style={{ width: '6%' }} />{/* Thx2 Consumed */}
            <col style={{ width: '6.5%' }} />{/* Flare TIME */}
            <col style={{ width: '4.5%' }} />{/* Flare Total */}
            <col style={{ width: '5.5%' }} />{/* Remarks */}
          </colgroup>
          <tr>
            <th rowSpan={3} style={TH}>Date</th>
            <th colSpan={4} style={TH}>Gas Engine Power Meter, kWh</th>
            <th colSpan={5} style={TH}>Gas Storage [%]</th>
            <th colSpan={4} style={TH}>Gas Flowmeters [Nm³]</th>
            <th colSpan={3} style={TH}>Gas Flare Operation</th>
          </tr>
          <tr>
            <th colSpan={2} style={TH}>Engine 1, Totalizer</th>
            <th colSpan={2} style={TH}>Engine 2, Totalizer</th>
            <th style={TH}>F1</th><th style={TH}>F2</th><th style={TH}>F3</th>
            <th style={TH}>Total</th><th style={TH}>% Methane</th>
            <th colSpan={2} style={TH}>Boiler Consumption<br/>(Thermax 1)</th>
            <th colSpan={2} style={TH}>Boiler Consumption<br/>(Thermax 2)</th>
            <th rowSpan={2} style={TH}>TIME</th>
            <th rowSpan={2} style={TH}>Total<br/>[Nm³]</th>
            <th rowSpan={2} style={TH}>Remarks<br/>(Flaring<br/>Hrs)</th>
          </tr>
          <tr>
            <th style={TH}>Totalizer</th><th style={TH}>Output</th>
            <th style={TH}>Totalizer</th><th style={TH}>Output</th>
            <th style={TH}>{fc(FERMENTER_CAP_NM3)}</th>
            <th style={TH}>{fc(FERMENTER_CAP_NM3)}</th>
            <th style={TH}>{fc(FERMENTER_CAP_NM3)}</th>
            <th style={TH}>{fc(FERMENTER_CAP_NM3 * 3)}</th>
            <th style={TH}></th>
            <th style={TH}>Totalizer</th><th style={TH}>Consumed</th>
            <th style={TH}>Totalizer</th><th style={TH}>Consumed</th>
          </tr>
        </thead>
        <tbody>
          {prevDayRow && renderRow(prevDayRow, 'Previous day value', false, '#f5f5f5')}
          {rows.map(row => renderRow(row))}
          {summaryRow && renderRow(summaryRow, summaryLabel ?? 'Total', true, '#dcedc8')}
        </tbody>
      </table>

      {/* Page 4 bottom summary */}
      {summaryMetadata && (
        <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 2 }}>
          {/* Gas Generation */}
          <Box>
            <Typography sx={{ fontSize: '9px', fontWeight: 700, mb: 0.5 }}>Gas Generation &amp; Consumption</Typography>
            <table style={{ borderCollapse: 'collapse', fontSize: '9px', width: '100%' }}>
              <tbody>
                {[
                  ['Total Gas Production', summaryMetadata.totalGasProduction, '[Nm³]'],
                  ['Boiler Gas Consumption', summaryMetadata.boilerGasConsumption, '[Nm³]'],
                  ['Flared Gas', summaryMetadata.flaredGas, '[Nm³]'],
                ].map(([label, val, unit]) => (
                  <tr key={String(label)}>
                    <td style={{ ...TD, textAlign: 'left' }}>{label}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>
                      {val != null ? fc(val as number) : '—'}
                    </td>
                    <td style={{ ...TD, color: '#555' }}>{unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          {/* Gas Inventory */}
          <Box>
            <Typography sx={{ fontSize: '9px', fontWeight: 700, mb: 0.5 }}>Gas Inventory</Typography>
            <table style={{ borderCollapse: 'collapse', fontSize: '9px', width: '100%' }}>
              <tbody>
                <tr><td style={{ ...TD, textAlign: 'left' }}>Initial</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fc(summaryMetadata.gasInventoryInitial)}</td>
                  <td style={{ ...TD, color: '#555' }}>[m³]</td></tr>
                <tr><td style={{ ...TD, textAlign: 'left' }}>Final</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>
                    {summaryMetadata.gasInventoryFinal != null
                      ? (summaryMetadata.gasInventoryFinal < 0
                          ? `(${fc(Math.abs(summaryMetadata.gasInventoryFinal))})`
                          : fc(summaryMetadata.gasInventoryFinal))
                      : '—'}
                  </td>
                  <td style={{ ...TD, color: '#555' }}>[m³]</td></tr>
                <tr><td style={{ ...TD, textAlign: 'left' }}>Variance</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>0%</td>
                  <td style={TD}></td></tr>
              </tbody>
            </table>
          </Box>
          {/* Power Generation */}
          <Box>
            <Typography sx={{ fontSize: '9px', fontWeight: 700, mb: 0.5 }}>Power Generation</Typography>
            <table style={{ borderCollapse: 'collapse', fontSize: '9px', width: '100%' }}>
              <tbody>
                {[
                  ['Final Reading',   summaryMetadata.powerFinalReading,   '[kWh]'],
                  ['Initial Reading', summaryMetadata.powerInitialReading, '[kWh]'],
                  ['Net Generation',  summaryMetadata.netGeneration,       '[kWh]'],
                ].map(([label, val, unit]) => (
                  <tr key={String(label)}>
                    <td style={{ ...TD, textAlign: 'left' }}>{label}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{val != null ? fc(val as number) : '—'}</td>
                    <td style={{ ...TD, color: '#555' }}>{unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Signatures — only on last page (when summaryMetadata is present) */}
      {summaryMetadata && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <SigBlock label="Prepared by" name="Francis David A. Requillo" title="SBVC Representatives" />
          <SigBlock label="Reviewed and Checked by" name="Veerland Dongon" title="SBVC/Metpower Representatives" />
          <SigBlock label="Confirmed by" name="" title="Dole Representatives" date={REPORT_DATE} />
        </Box>
      )}
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const MonthlyBillingReport: React.FC = () => {
  const navigate = useNavigate();
  const [repMonth, setRepMonth] = useState(new Date().getMonth() + 1);
  const [repYear,  setRepYear]  = useState(new Date().getFullYear());
  const [biogasReport, setBiogasReport] = useState<any>(null);
  const [fostacRows,   setFostacRows]   = useState<any[]>([]);
  const [effectiveRates, setEffectiveRates] = useState<{ power: number; gas: number }>({ power: 7.5, gas: 13.5 });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(async (year: number, month: number) => {
    setLoading(true); setError('');
    const ym = `${year}-${String(month).padStart(2, '0')}`;
    try {
      const [bioRes, fostacRes, ratesRes] = await Promise.all([
        axios.get(`${API}/daily-biogas/monthly-report`, { params: { month: ym } }),
        axios.get(`${API}/fostac/monthly-report`, { params: { month: ym } }).catch(() => ({ data: [] })),
        axios.get(`${API}/billing-rates/effective`, { params: { month: ym } }).catch(() => ({ data: { power: 7.5, gas: 13.5 } })),
      ]);
      setBiogasReport(bioRes.data);
      setFostacRows(Array.isArray(fostacRes.data) ? fostacRes.data : []);
      setEffectiveRates(ratesRes.data ?? { power: 7.5, gas: 13.5 });
    } catch { setError('Failed to load report data.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(repYear, repMonth); }, [repYear, repMonth, load]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const cutoff  = biogasReport?.cutoff  ?? null;
  const rows: any[] = biogasReport?.rows ?? [];
  const totals  = biogasReport?.totals  ?? {};
  const lastRow = rows[rows.length - 1] ?? null;

  // Methane by date (prefer DS shift)
  const methaneMap: Record<string, number | null> = {};
  for (const r of fostacRows) {
    if (r.shift === 'DS' && r.gqCh4 != null) methaneMap[r.readingDate] = r.gqCh4;
    else if (r.shift === 'NS' && methaneMap[r.readingDate] == null && r.gqCh4 != null)
      methaneMap[r.readingDate] = r.gqCh4;
  }

  const powerBillingRate = effectiveRates.power;
  const gasBillingRate   = effectiveRates.gas;

  // ── Page 1 (Power) ────────────────────────────────────────────────────────
  const eng1Initial  = cutoff?.engine1Totalizer   ?? null;
  const eng1Final    = lastRow?.engine1Totalizer   ?? null;
  const eng1Consumed = totals.engine1Output         ?? (eng1Final != null && eng1Initial != null ? eng1Final - eng1Initial : null);
  const eng2Initial  = cutoff?.engine2Totalizer   ?? null;
  const eng2Final    = lastRow?.engine2Totalizer   ?? null;
  const eng2Consumed = totals.engine2Output         ?? (eng2Final != null && eng2Initial != null ? eng2Final - eng2Initial : null);
  const totalPower   = (eng1Consumed ?? 0) + (eng2Consumed ?? 0);

  // ── Page 2 (Gas) ──────────────────────────────────────────────────────────
  const thx1Initial  = cutoff?.thermax1Totalizer  ?? null;
  const thx1Final    = lastRow?.thermax1Totalizer  ?? null;
  const thx1Consumed = totals.thermax1Output        ?? (thx1Final != null && thx1Initial != null ? thx1Final - thx1Initial : null);
  const thx2Initial  = cutoff?.thermax2Totalizer  ?? null;
  const thx2Final    = lastRow?.thermax2Totalizer  ?? null;
  const thx2Consumed = totals.thermax2Output        ?? (thx2Final != null && thx2Initial != null ? thx2Final - thx2Initial : null);
  const totalGas     = (thx1Consumed ?? 0) + (thx2Consumed ?? 0);

  // ── Pages 3 & 4 row split ─────────────────────────────────────────────────
  const SPLIT = 15;
  const page3Rows = rows.slice(0, SPLIT);
  const page4Rows = rows.slice(SPLIT);

  // Each half needs its own "subtotal" output sums with last totalizer readings
  const buildAccumRow = (subset: any[]) => {
    if (!subset.length) return null;
    const last = subset[subset.length - 1];
    const sum = (key: string) => subset.reduce((s: number, r: any) => s + (r[key] ?? 0), 0);
    return {
      ...last,
      engine1Output: sum('engine1Output'),
      engine2Output: sum('engine2Output'),
      thermax1Output: sum('thermax1Output'),
      thermax2Output: sum('thermax2Output'),
      gasFlareTotalVolume: sum('gasFlareTotalVolume'),
      remarks: '',
    };
  };
  const subtotalPage3 = buildAccumRow(page3Rows);
  // For page 4: "Total from Page 1" is the subtotal of page 3 rows
  // The page 4 "Total" uses biogasReport.totals

  // ── Summary section (bottom of page 4) ───────────────────────────────────
  const flaredGas   = rows.reduce((s: number, r: any) => s + (r.gasFlareTotalVolume ?? 0), 0);
  const gasInvInit  = gasInventoryNm3(cutoff);
  const gasInvFinal = gasInventoryNm3(lastRow);
  const gasInvChange = gasInvFinal != null && gasInvInit != null ? gasInvFinal - gasInvInit : null;
  const totalGasProd = gasInvChange != null ? totalGas + flaredGas + gasInvChange : null;

  const powerFinalReading   = (eng1Final   ?? 0) + (eng2Final   ?? 0);
  const powerInitialReading = (eng1Initial ?? 0) + (eng2Initial ?? 0);

  const summaryMetadata = {
    totalGasProduction: totalGasProd,
    boilerGasConsumption: totalGas,
    flaredGas,
    gasInventoryInitial: gasInvInit,
    gasInventoryFinal: gasInvFinal,
    powerFinalReading,
    powerInitialReading,
    netGeneration: powerFinalReading - powerInitialReading,
  };

  const pageStyle = {
    mb: 2, overflow: 'hidden',
    '@media print': { boxShadow: 'none', border: 'none', mb: 0 },
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Controls (screen only) ── */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', '@media print': { display: 'none' } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small" variant="outlined">Back</Button>
        <AssessmentIcon sx={{ color: '#1b5e20', fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700} color="#1b5e20">Monthly Billing Report</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Month</InputLabel>
          <Select value={repMonth} label="Month" onChange={e => setRepMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>Year</InputLabel>
          <Select value={repYear} label="Year" onChange={e => setRepYear(Number(e.target.value))}>
            {buildYears().map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#2e7d32' } }}>
          Print / Save PDF
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, '@media print': { display: 'none' } }}>{error}</Alert>}
      {rows.length === 0 && !loading && !error && (
        <Alert severity="info" sx={{ mb: 2, '@media print': { display: 'none' } }}>
          No daily monitoring data for {MONTHS[repMonth - 1]} {repYear}.
          Enter data in the Daily Biogas Monitor to generate this report.
        </Alert>
      )}

      {/* ── Report pages ── */}
      {/* PAGE 1 — Power Billing */}
      <Paper elevation={2} className="billing-page" sx={pageStyle}>
        <BillingInvoice pageNum={1} year={repYear} month={repMonth} type="power"
          rows={[
            { label: 'Gas Engine 1', final: eng1Final, initial: eng1Initial, consumed: eng1Consumed, unit: 'kWh' },
            { label: 'Gas Engine 2', final: eng2Final, initial: eng2Initial, consumed: eng2Consumed, unit: 'kWh' },
          ]}
          totalConsumed={totalPower} unitPrice={powerBillingRate} totalAmount={totalPower * powerBillingRate} />
      </Paper>

      {/* PAGE 2 — Gas Billing */}
      <Paper elevation={2} className="billing-page" sx={pageStyle}>
        <BillingInvoice pageNum={2} year={repYear} month={repMonth} type="gas"
          rows={[
            { label: 'Thermax 1', final: thx1Final, initial: thx1Initial, consumed: thx1Consumed, unit: 'Nm³' },
            { label: 'Thermax 2', final: thx2Final, initial: thx2Initial, consumed: thx2Consumed, unit: 'Nm³' },
          ]}
          totalConsumed={totalGas} unitPrice={gasBillingRate} totalAmount={totalGas * gasBillingRate} />
      </Paper>

      {/* PAGE 3 — Daily Production (rows 1–15, or all rows if ≤15) */}
      <Paper elevation={2} className="production-page" sx={pageStyle}>
        <ProductionTable
          rows={page3Rows} methaneMap={methaneMap}
          prevDayRow={cutoff}
          summaryLabel={page4Rows.length === 0 ? 'Total' : 'Subtotal'}
          summaryRow={page4Rows.length === 0 ? totals : subtotalPage3}
          summaryMetadata={page4Rows.length === 0 ? summaryMetadata : undefined}
          year={repYear} month={repMonth} docNum={73}
        />
      </Paper>

      {/* PAGE 4 — Daily Production (rows 16–end + summary) */}
      {page4Rows.length > 0 && (
        <Paper elevation={2} className="production-page" sx={pageStyle}>
          <ProductionTable
            rows={page4Rows} methaneMap={methaneMap}
            prevDayRow={subtotalPage3 ? { ...subtotalPage3, readingDate: '' } : undefined}
            summaryLabel="Total"
            summaryRow={totals}
            summaryMetadata={summaryMetadata}
            year={repYear} month={repMonth} docNum={74}
          />
        </Paper>
      )}

      {/* Print global styles */}
      <style>{`
        @media screen {
          .billing-page, .production-page { zoom: 1.5; margin-bottom: 40px; }
        }
        @media print {
          @page portrait-page  { size: A4 portrait;  margin: 12mm 14mm; }
          @page landscape-page { size: A4 landscape; margin: 6mm 8mm; }

          /* Named pages so each section uses the right orientation */
          .billing-page    { page: portrait-page; }
          .production-page { page: landscape-page; }

          /* Page break ONLY between our 4 report pages — no breaks on nested MUI components */
          .billing-page    { break-after: page; }
          .production-page { break-after: page; }
          /* Last page in the document should not force an extra blank page */
          .production-page:last-of-type { break-after: avoid; }

          /* Reset screen zoom and margins */
          .billing-page, .production-page {
            zoom: 1 !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
            overflow: visible !important;
          }
          /* Ensure production table is never clipped */
          .production-page * { overflow: visible !important; }

          /* Hide sidebar, appbar, controls */
          .MuiDrawer-root, .MuiAppBar-root, header, nav { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default MonthlyBillingReport;
